import { DEFAULT_RUN_SETTINGS, parseRunSettings } from "@/lib/run-settings";
import { useEffect, useCallback, useRef, useState } from "react";
import { atom } from "jotai";
import { useAtomValue, useSetAtom, useStore } from "jotai";
import type { Edge } from "@xyflow/react";
import type { QueryClient } from "@tanstack/react-query";
import type { NodeDefinition, UpdateWorkflowRequest } from "@/generated";
import { WorkflowService } from "@/generated";
import { SpotToast } from "@/components/SpotToast";
import type {
  NoxusReactflowNodeV2,
  WorkflowDefinitionV2,
} from "@/types/spotflow-v2";
import {
  runSettingsAtom,
  nodesAtom,
  edgesAtom,
  activeFlowIdAtom,
  editorRevisionAtom,
  saveStateAtom,
  hasQueueToSaveAtom,
  hasUnsavedUserEditAtom,
  saveWorkflowHashAtom,
  saveBaseRevisionAtom,
  saveConflictAtom,
  selectedNodeIdAtom,
  runNodeStatusesAtom,
  isValidDataAtom,
  previewRunIdAtom,
  previewSnapshotAtom,
  initialNodes,
  initialEdges,
  isEditorReadOnlyAtom,
  loadedVersionIdAtom,
  dynamicNodeConfigsAtom,
  areConfigEndpointsLoadedAtom,
  nodeConfigErrorsAtom,
  flowLoadGenerationAtom,
  pendingLayoutFlowIdAtom,
} from "../atoms";
import {
  markDirty,
  serializeDefinitionV2,
  definitionNodeToReactFlow,
  hydrateDefinitionEdges,
} from "../helpers";
import { triggerValidationAtom } from "./validation";
import { historyStackAtom } from "../history/atoms";
import { withoutHistory } from "../history/recordTransaction";

/**
 * Deep-strip values that aren't safely JSON-serializable (DOM nodes, React
 * fibers, functions, etc.) and break cycles. Used as a defensive shield
 * before the autosave hands the workflow definition to axios — without it,
 * any latent in-place mutation that smuggled a DOM element onto a node's
 * data crashes the save with `Converting circular structure to JSON` and
 * traps the editor.
 */
function sanitizeForJson<T>(value: T, seen = new WeakSet<object>()): T {
  if (value === null || typeof value !== "object") {
    if (typeof value === "function") return undefined as unknown as T;
    return value;
  }
  if (typeof Node !== "undefined" && value instanceof Node) {
    return null as unknown as T;
  }
  if (seen.has(value as object)) return null as unknown as T;
  seen.add(value as object);
  if (Array.isArray(value)) {
    return value.map((v) => sanitizeForJson(v, seen)) as unknown as T;
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    // Skip React internal properties that are appended to DOM elements.
    if (k.startsWith("__reactFiber") || k.startsWith("__reactProps")) continue;
    out[k] = sanitizeForJson(v, seen);
  }
  return out as unknown as T;
}

// ── Reset editor ──────────────────────────────────────────────────────────

/** The only supported way to flag the editor dirty from a component.
 *  Writing `hasQueueToSaveAtom` directly is a no-op when it is already
 *  true, which leaves the flag set with no save scheduled behind it. */
export const markDirtyAtom = atom(null, (_get, set) => markDirty(set));

export const resetEditorAtom = atom(null, (get, set) => {
  withoutHistory(get, set, () => {
    set(runSettingsAtom, DEFAULT_RUN_SETTINGS);
    set(nodesAtom, initialNodes);
    set(edgesAtom, initialEdges);
    set(activeFlowIdAtom, undefined);
    set(editorRevisionAtom, (revision) => revision + 1);
    set(hasQueueToSaveAtom, false);
    set(hasUnsavedUserEditAtom, false);
    set(saveWorkflowHashAtom, undefined);
    set(saveBaseRevisionAtom, undefined);
    set(loadedVersionIdAtom, null);
    // Don't carry a stale conflict dialog into the next flow we open.
    set(saveConflictAtom, false);
    set(saveStateAtom, { timestamp: undefined, status: "not_saved" });
    set(selectedNodeIdAtom, null);
    set(runNodeStatusesAtom, {});
    set(isValidDataAtom, undefined);
    set(previewRunIdAtom, null);
    set(previewSnapshotAtom, null);
    set(historyStackAtom, { undo: [], redo: [] });
    // Belongs to the canvas being torn down — consumers must wait for the next
    // flow's batch load rather than read the previous flow's outputs.
    set(dynamicNodeConfigsAtom, new Map());
    set(nodeConfigErrorsAtom, new Map());
    set(areConfigEndpointsLoadedAtom, false);
  });
});

// ── Save workflow ─────────────────────────────────────────────────────────

// Serializes saves, forced ones included: a slow PATCH must finish (and
// refresh the base hash) before the next one starts. Otherwise the next save
// sends a stale hash and 409s against our *own* just-written version.
let saveChain: Promise<void> = Promise.resolve();

export const saveWorkflowAtom = atom(
  null,
  async (
    get,
    set,
    args: {
      spotGroupId: string;
      workflowId: string;
      queryClient?: QueryClient;
      // Force overwrite, bypassing the server's concurrent-edit (409) check.
      // Set by the conflict dialog's "Overwrite" action.
      force?: boolean;
    }
  ) => {
    const { spotGroupId, workflowId, queryClient, force } = args;

    const doSave = async (): Promise<void> => {
      if (get(activeFlowIdAtom) !== workflowId) return;

      // Hard guard: the editor is read-only when previewing a past run or
      // viewing a saved version. Even if every UI gate misses an edit path,
      // we must not PATCH the live workflow with a snapshot's state.
      if (get(isEditorReadOnlyAtom)) {
        set(hasQueueToSaveAtom, false);
        return;
      }

      set(saveStateAtom, {
        timestamp: new Date().toISOString(),
        status: "saving",
      });

      // Read state at execution time (after any prior chained save) so we
      // serialize the latest nodes/edges and the freshly-refreshed hash.
      const revision = get(editorRevisionAtom);
      const loadGeneration = get(flowLoadGenerationAtom);
      const nodes = get(nodesAtom);
      const edges = get(edgesAtom);
      const hash = get(saveWorkflowHashAtom);
      const baseRevision = get(saveBaseRevisionAtom);

      const definition = sanitizeForJson(
        serializeDefinitionV2(nodes, edges, get(runSettingsAtom))
      );

      try {
        const req: UpdateWorkflowRequest = {
          definition:
            definition as unknown as UpdateWorkflowRequest["definition"],
          workflow_hash: hash,
          base_revision: baseRevision,
          // Echo of the save target. The server rejects the PATCH when this
          // disagrees with the URL — the last line of defense against a
          // payload built for one flow being saved onto another. Not
          // bypassable with force.
          expected_workflow_id: workflowId,
        };

        const res = await WorkflowService.updateWorkflow({
          path: { group_id: spotGroupId, workflow_id: workflowId },
          query: force ? { force: true } : undefined,
          body: req,
        });

        // The PATCH is async and can be slow on a bad connection. If the user
        // navigated to another flow while it was in flight, the success
        // bookkeeping below would transplant THIS flow's server hash and its
        // cleared save-state/dirty flag onto whatever flow is now active — the
        // active flow then autosaves itself over this one (the "flow became a
        // copy of the other tab" bug). Only commit to the shared atoms if we're
        // still the active flow; the server write already succeeded regardless,
        // so we still refresh the query cache.
        if (get(activeFlowIdAtom) !== workflowId) {
          queryClient?.invalidateQueries({ queryKey: ["WORKFLOWS"] });
          queryClient?.invalidateQueries({ queryKey: ["WORKFLOWS_ID"] });
          return;
        }

        // A reload of this same flow (exiting a run preview with no
        // trustworthy snapshot) installs the server's own baseline and is
        // invisible to the flow-id check above. Stamping our hash over it
        // leaves the next save comparing against a version nobody is editing,
        // where it can no longer raise a 409.
        const reloaded = get(flowLoadGenerationAtom) !== loadGeneration;

        // Track the server's new hash/revision so the next save compares
        // against the version we just wrote — without this the base drifts
        // and the 409 conflict check mis-fires (V1 does the same).
        if (!reloaded) {
          const nextHash = res.data?.workflow_hash;
          if (nextHash) set(saveWorkflowHashAtom, nextHash);
          const nextRevision = res.data?.revision;
          if (nextRevision !== undefined)
            set(saveBaseRevisionAtom, nextRevision);
        }

        set(saveConflictAtom, false);
        // Edits made while the PATCH was in flight are NOT in what we just
        // wrote. Reporting "saved" would also clear the dirty flag, and the
        // autosave that would have persisted them never fires again.
        if (!reloaded && get(editorRevisionAtom) === revision) {
          set(saveStateAtom, {
            timestamp: new Date().toISOString(),
            status: "saved",
          });
          set(hasQueueToSaveAtom, false);
          set(hasUnsavedUserEditAtom, false);
        }

        queryClient?.invalidateQueries({ queryKey: ["WORKFLOWS"] });
        queryClient?.invalidateQueries({ queryKey: ["WORKFLOWS_ID"] });
      } catch (e) {
        // If we've since navigated away, the conflict/error belongs to a flow
        // that's no longer on screen — don't raise its conflict dialog over the
        // active flow or stomp the active flow's save state.
        if (get(activeFlowIdAtom) !== workflowId) return;
        // 409 = someone else saved a newer version. Surface the conflict
        // dialog instead of a generic error so the user can
        // overwrite / discard / fork.
        const status = (e as { response?: { status?: number } })?.response
          ?.status;
        if (status === 409 && !force && !get(hasUnsavedUserEditAtom)) {
          // Only derived changes were pending (an auto-layout, iterate flags):
          // the newer version re-derives them, so take it instead of asking
          // the user to resolve a conflict they did not cause.
          set(hasQueueToSaveAtom, false);
          set(saveStateAtom, {
            timestamp: new Date().toISOString(),
            status: "saved",
          });
          queryClient?.invalidateQueries({ queryKey: ["WORKFLOWS_ID"] });
          return;
        }
        if (status === 409) {
          set(saveConflictAtom, true);
          set(saveStateAtom, {
            timestamp: new Date().toISOString(),
            status: "errored",
          });
          return;
        }
        console.error(e);
        // One toast per outage, not one per retry.
        SpotToast.error(
          "There was an error saving the workflow",
          undefined,
          undefined,
          "workflow-save-error"
        );
        set(saveStateAtom, {
          timestamp: new Date().toISOString(),
          status: "errored",
        });
      }
    };

    // Continue the queue even when an earlier save failed.
    saveChain = saveChain.then(doSave, doSave);
    await saveChain;
    return (
      get(activeFlowIdAtom) === workflowId &&
      !get(isEditorReadOnlyAtom) &&
      get(saveStateAtom).status === "saved" &&
      !get(hasQueueToSaveAtom)
    );
  }
);

// ── Save current state as a new named version ───────────────────────────────

export const saveAsNewVersionAtom = atom(
  null,
  async (
    get,
    _set,
    args: { spotGroupId: string; workflowId: string; name: string }
  ) => {
    const { spotGroupId, workflowId, name } = args;
    const nodes = get(nodesAtom);
    const edges = get(edgesAtom);
    const definition = sanitizeForJson(
      serializeDefinitionV2(nodes, edges, get(runSettingsAtom))
    );
    await WorkflowService.createWorkflowVersion({
      path: { group_id: spotGroupId, workflow_id: workflowId },
      body: {
        name,
        definition: definition as unknown as Record<string, unknown>,
      },
    });
  }
);

// ── Load workflow ─────────────────────────────────────────────────────────

export const loadWorkflowAtom = atom(
  null,
  (
    get,
    set,
    args: {
      definition: WorkflowDefinitionV2;
      nodeRegistry: NodeDefinition[];
      flowId: string;
      workflowHash?: string;
      revision?: number;
      spotGroupId: string;
      versionId?: string | null;
      // Bypass the identity skip-guard and re-hydrate unconditionally. For
      // recovery paths where the canvas is known to be untrustworthy even
      // though flowId/hash/versionId all match (e.g. exiting a run preview
      // without a restorable snapshot).
      force?: boolean;
    }
  ) => {
    const {
      definition,
      nodeRegistry,
      flowId,
      workflowHash,
      revision,
      spotGroupId,
      versionId = null,
      force = false,
    } = args;

    if (
      !force &&
      workflowHash !== undefined &&
      flowId === get(activeFlowIdAtom) &&
      workflowHash === get(saveWorkflowHashAtom) &&
      versionId === get(loadedVersionIdAtom)
    ) {
      // Same content, possibly a newer server revision (e.g. a force save
      // that wrote identical bytes) — adopt it so the next save's base isn't
      // stale.
      if (revision !== undefined) set(saveBaseRevisionAtom, revision);
      return;
    }

    set(resetEditorAtom);

    const nodes: NoxusReactflowNodeV2[] = definition.nodes.map((defNode) => {
      const registryNode = nodeRegistry.find((r) => r.type === defNode.type);
      return definitionNodeToReactFlow(defNode, registryNode);
    });

    const edges: Edge[] = hydrateDefinitionEdges(definition, nodeRegistry);

    withoutHistory(get, set, () => {
      set(nodesAtom, nodes);
      set(edgesAtom, edges);
      set(runSettingsAtom, parseRunSettings(definition));
      set(activeFlowIdAtom, flowId);
      set(saveWorkflowHashAtom, workflowHash);
      set(saveBaseRevisionAtom, revision);
      set(loadedVersionIdAtom, versionId);
      set(
        pendingLayoutFlowIdAtom,
        definition.layout === "pending" ? flowId : null
      );
      set(saveStateAtom, { timestamp: undefined, status: "saved" });
      set(historyStackAtom, { undo: [], redo: [] });
    });

    set(flowLoadGenerationAtom, get(flowLoadGenerationAtom) + 1);
    set(triggerValidationAtom, spotGroupId);
  }
);

// ── Load workflow preview (read-only, no reset) ───────────────────────────

export const loadWorkflowPreviewAtom = atom(
  null,
  (
    get,
    set,
    args: {
      definition: WorkflowDefinitionV2;
      nodeRegistry: NodeDefinition[];
    }
  ) => {
    const { definition, nodeRegistry } = args;

    const nodes: NoxusReactflowNodeV2[] = definition.nodes.map((defNode) => {
      const registryNode = nodeRegistry.find((r) => r.type === defNode.type);
      return definitionNodeToReactFlow(defNode, registryNode);
    });

    const edges: Edge[] = hydrateDefinitionEdges(definition, nodeRegistry);

    withoutHistory(get, set, () => {
      set(nodesAtom, nodes);
      set(edgesAtom, edges);
    });
  }
);

// ── Auto-save hook ────────────────────────────────────────────────────────

const AUTOSAVE_DEBOUNCE_MS = 1200;
// Ceiling on how long an edit may sit unsaved. Each edit restarts the
// debounce, so without this someone typing with no gap longer than it keeps
// pushing the save out for as long as they keep going.
const AUTOSAVE_MAX_WAIT_MS = 5000;
// A failed save leaves the editor dirty, so the hook would otherwise retry
// (and toast) every debounce for as long as the backend stays down.
const AUTOSAVE_RETRY_CAP_MS = 30_000;

export function useAutoSave(save: () => Promise<void>) {
  const store = useStore();
  const hasQueueToSave = useAtomValue(hasQueueToSaveAtom);
  const editorRevision = useAtomValue(editorRevisionAtom);
  const workflowId = useAtomValue(activeFlowIdAtom);
  const isReadOnly = useAtomValue(isEditorReadOnlyAtom);
  // Pause autosave while a conflict dialog is open — otherwise it would keep
  // re-firing the same rejected save and re-raising the 409 every 1.2s.
  const hasConflict = useAtomValue(saveConflictAtom);
  const dirtySinceRef = useRef<number | null>(null);
  const inFlightRef = useRef(false);
  const failedSavesRef = useRef(0);
  const lastRevisionRef = useRef(editorRevision);
  const [settledSaves, setSettledSaves] = useState(0);

  // `editorRevision` is load-bearing in the dependencies below: an edit made
  // while the editor is already dirty leaves `hasQueueToSave` at true, which
  // Jotai reads as no change, so without it nothing re-runs and that edit is
  // never saved.
  useEffect(() => {
    if (isReadOnly || hasConflict || !hasQueueToSave || !workflowId) {
      dirtySinceRef.current = null;
      return;
    }
    // One save at a time. Without this the ceiling below queues a fresh
    // PATCH of the whole definition every few seconds against a slow link,
    // and every queued save but the last is redundant anyway.
    if (inFlightRef.current) return;
    if (lastRevisionRef.current !== editorRevision) {
      lastRevisionRef.current = editorRevision;
      failedSavesRef.current = 0;
    }
    dirtySinceRef.current ??= Date.now();
    const waited = Date.now() - dirtySinceRef.current;
    const debounce = Math.max(
      0,
      Math.min(AUTOSAVE_DEBOUNCE_MS, AUTOSAVE_MAX_WAIT_MS - waited)
    );
    const backoff =
      failedSavesRef.current === 0
        ? 0
        : Math.min(
            AUTOSAVE_RETRY_CAP_MS,
            AUTOSAVE_DEBOUNCE_MS * 2 ** failedSavesRef.current
          );
    const delay = Math.max(debounce, backoff);
    const timeout = setTimeout(() => {
      // Start the next window here rather than on the dirty flag clearing:
      // a save that lands stale leaves the editor dirty, and the deadline
      // would then be in the past for every later keystroke.
      dirtySinceRef.current = null;
      inFlightRef.current = true;
      const settle = () => {
        inFlightRef.current = false;
        // The save chain swallows every error to self-heal, so the promise
        // cannot say whether this attempt failed; the save state can.
        failedSavesRef.current =
          store.get(saveStateAtom).status === "errored"
            ? failedSavesRef.current + 1
            : 0;
        // Re-arm: the editor may have gone dirty again while this ran.
        setSettledSaves((count) => count + 1);
      };
      try {
        void Promise.resolve(save()).then(settle, settle);
      } catch {
        // Unreachable while every caller is async, which converts throws into
        // rejections. Kept because a caller that ever threw synchronously
        // would strand the in-flight flag and kill autosave for the session.
        settle();
      }
    }, delay);
    return () => clearTimeout(timeout);
  }, [
    isReadOnly,
    hasConflict,
    hasQueueToSave,
    editorRevision,
    settledSaves,
    workflowId,
    save,
    store,
  ]);
}

// ── Save helper hook ──────────────────────────────────────────────────────

export function useSave(spotGroupId: string, queryClient: QueryClient) {
  const workflowId = useAtomValue(activeFlowIdAtom);
  const triggerSave = useSetAtom(saveWorkflowAtom);
  const triggerValidation = useSetAtom(triggerValidationAtom);

  const save = useCallback(
    async (opts?: { force?: boolean }) => {
      if (!workflowId) return;
      await triggerSave({
        spotGroupId,
        workflowId,
        queryClient,
        force: opts?.force,
      });
      triggerValidation(spotGroupId);
    },
    [workflowId, triggerSave, spotGroupId, queryClient, triggerValidation]
  );

  return save;
}
